<template>
    <SmuMainMenu :sections="filteredSections" :current-route="route.path" />
    <div class="mt-4">
        <MenuRedirect />
    </div>
</template>

<script setup lang="ts">
import { SmuMainMenu } from "@smakecloud/smake-ui";

interface MenuIcon {
    icon: string;
    type: string;
}

interface MenuLink {
    name: string;
    icon: MenuIcon;
    url: string;
    devBadge?: boolean;
    children?: MenuLink[];
}

interface MenuSection {
    name: string;
    links: MenuLink[];
}

const { t } = useI18n();
const { isAuthorised } = useAuthorisationChecker();
const route = useRoute();

const router = useRouter();

const sections = computed<MenuSection[]>(() => [
    {
        name: "",
        links: [
            {
                name: t("shop/backend.dashboard.menu.title"),
                icon: {
                    icon: "tachometer-alt",
                    type: "light",
                },
                url: `/dashboard`,
            },
        ],
    },
    {
        name: t("shop/backend.sidebar.catalogs"),
        links: [
            {
                name: t("shop/backend/apps.menu.title"),
                icon: {
                    icon: "cart-shopping",
                    type: "light",
                },
                url: `/shops`,
            },
        ],
    },
    {
        name: t("shop/backend.sidebar.settings"),
        links: [
            {
                name: t("logo_ai_check"),
                devBadge: true,
                icon: {
                    icon: "microchip-ai",
                    type: "light",
                },
                url: `/ai-settings`,
            },
            {
                name: t("helpers.common.pricelist", 2),
                children: [
                    {
                        name: t("helpers.common.production"),
                        icon: {
                            icon: "arrow-turn-down-right",
                            type: "light",
                        },
                        url: `/pricelists/production`,
                    },
                    {
                        name: t("helpers.common.merchant", 2),
                        icon: {
                            icon: "arrow-turn-down-right",
                            type: "light",
                        },
                        url: `/pricelists/merchants`,
                    },
                ],
                icon: {
                    icon: "boxes-stacked",
                    type: "light",
                },
                url:
                    route.path === "/pricelists/merchants"
                        ? "/pricelists/merchants"
                        : "/pricelists/production",
            },
            {
                name: t("color", 2),
                children: [
                    {
                        name: t("colortype", 2),
                        icon: {
                            icon: "arrow-turn-down-right",
                            type: "light",
                        },
                        url: `/colortypes`,
                    },
                ],
                icon: {
                    icon: "palette",
                    type: "light",
                },
                url: `/colors`,
            },
            {
                name: t("helpers.common.production_method", 2),
                icon: {
                    icon: "gem",
                    type: "light",
                },
                url: `/production-methods`,
            },
        ],
    },
]);

function isLinkAuthorised(url: string) {
    const resolvedRoute = router.resolve(url);

    return isAuthorised({
        dev: resolvedRoute.meta.dev,
        feature: resolvedRoute.meta.feature,
        permission: resolvedRoute.meta.permission,
    });
}

function filterLink(link: MenuLink): MenuLink {
    if (!link.children) return link;

    return {
        ...link,
        children: link.children.filter((child) => isLinkAuthorised(child.url)),
    };
}

const filteredSections = computed(() =>
    sections.value
        .filter((section) =>
            section.links.some((link) => isLinkAuthorised(link.url)),
        )
        .map((section) => ({
            ...section,
            links: section.links
                .filter((link) => isLinkAuthorised(link.url))
                .map(filterLink),
        })),
);
</script>
